// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for viewing Maryland bill amendments/formatting data.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import type { ProcessedAmendmentData, PageAmendment } from '../../models/types'
import type { FormattingData } from '../../services/maryland-bills.service'
import { AmendmentSummaryCard } from './AmendmentSummaryCard'
import { AmendmentsByPageDisplay } from './AmendmentsByPageDisplay'

export function processFormatting(
  formatting: FormattingData,
  billNumber: string,
  session: string,
  version: number,
): ProcessedAmendmentData {
  const amendments: PageAmendment[] = []
  let strikeoutCount = 0
  let underlineCount = 0
  let italicsCount = 0
  const pagesWithAmendments = new Set<number>()

  for (const [pageKey, entries] of Object.entries(formatting)) {
    const pageNumber = parseInt(pageKey.replace('page_', ''), 10)
    if (isNaN(pageNumber)) continue

    for (const entry of entries) {
      const types: ('strikeout' | 'underline' | 'italics')[] = []
      if (entry.formatting.includes('strike')) { types.push('strikeout'); strikeoutCount++ }
      if (entry.formatting.includes('underline')) { types.push('underline'); underlineCount++ }
      if (entry.formatting.includes('italics')) { types.push('italics'); italicsCount++ }

      if (types.length > 0) {
        pagesWithAmendments.add(pageNumber)
        amendments.push({ types, text: entry.text, pageNumber, lineNumber: entry.line_number })
      }
    }
  }

  return {
    billNumber,
    sessionCode: session,
    versionNumber: version,
    totalPages: Object.keys(formatting).length,
    summary: { totalAmendments: amendments.length, strikeoutCount, underlineCount, italicsCount, pagesWithAmendments: pagesWithAmendments.size },
    amendments,
  }
}

interface BillAmendmentsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  amendmentData: ProcessedAmendmentData | null
  billNumber: string
  loading: boolean
  error: string | null
  testId?: string
}

export const BillAmendmentsDialog: FC<BillAmendmentsDialogProps> = ({
  open,
  onOpenChange,
  amendmentData,
  billNumber,
  loading,
  error,
  testId,
}) => {
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading amendments...</span>
        </div>
      )
    }
    if (error) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Error</h3>
          <p className="text-muted-foreground">{error}</p>
        </div>
      )
    }
    if (!amendmentData || amendmentData.summary.totalAmendments === 0) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
          <h3 className="text-lg font-medium mb-2">No Amendments</h3>
          <p className="text-muted-foreground">
            No formatting data indicating amendments was found.
          </p>
        </div>
      )
    }
    return (
      <div className="space-y-4 p-1">
        <AmendmentSummaryCard amendmentData={amendmentData} />
        <AmendmentsByPageDisplay amendments={amendmentData.amendments} />
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-6xl h-[80vh] flex flex-col overflow-hidden"
        data-testid={testId}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{billNumber} — Amendments</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 mt-4">
          <ScrollArea className="h-full">{renderContent()}</ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}
